import type {
  WatchMarketSignalSelection,
  WatchMomentumDraft,
  WatchMovingAverageDraft,
} from "../hooks/useWatchAuthoringState";
import {
  getAuthorableWatchSignals,
  type WatchSignalCatalog,
} from "./watchSignalCatalog";
import {
  normalizeWatchCanonicalListingIdentity,
  type WatchCanonicalListingIdentity,
} from "./watchListingIdentity";
import {
  getReviewedSignalPresenter,
  isCompletedDailyVolatilityPresenter,
  isMovingAveragePresenter,
  isStandardizedOptionChainPresenter,
} from "./watchSignalReleaseRegistry";

/** Customer schedule facts accepted by the readable signal draft. */
export interface MarketSignalPolicyInput {
  /** Maximum active lifetime in whole days. */
  maxDurationDays: number;
  /** Deterministic evaluation cadence in whole minutes. */
  evaluationIntervalMinutes: number;
  /** Maximum eligible alert deliveries. */
  maxAlertsPerWatch: number;
  /** Minimum whole-minute gap between repeated alerts. */
  cooldownMinutes: number;
  /** Whether evaluations are limited to regular U.S. market hours. */
  marketHoursOnly: boolean;
}

/** Inputs needed to build the one customer-readable MARKET request. */
export interface MarketSignalPreviewInput {
  /** Runtime-decoded authenticated server catalog. */
  catalog: WatchSignalCatalog | null;
  /** Exact explicit catalog selection. */
  signal: WatchMarketSignalSelection | null;
  /** Progressive Moving averages choices; used instead of a hidden generic signal selection. */
  movingAverageDraft?: WatchMovingAverageDraft | null;
  /** Progressive completed-daily Momentum choices. */
  momentumDraft?: WatchMomentumDraft | null;
  /** Lookup-confirmed underlying ticker. */
  ticker: string;
  /** Canonical listing captured from the selected authenticated suggestion. */
  listingIdentity: WatchCanonicalListingIdentity | null;
  /** Explicit deterministic comparison. */
  operator: "gte" | "lte" | null;
  /** Decimal threshold preserved as text. */
  threshold: string;
  /** Explicit Watch schedule and delivery lifecycle. */
  policy: MarketSignalPolicyInput;
}

interface MarketSignalWatchDraftBaseV1 {
  /** Readable draft schema. */
  schema_version: 1;
  /** Trade-stage job. */
  job: "MARKET";
  /** Exact reviewed behavior version. */
  definition_version: 1;
  /** Customer-understandable subject scope. */
  subject_scope: "UNDERLYING";
  /** Watch schedule and delivery lifecycle. */
  policy: {
    max_duration_days: number;
    evaluation_interval_minutes: number;
    max_alerts_per_watch: number;
    cooldown_minutes: number;
    market_hours_only: boolean;
  };
}

/** Strict exact-price draft retained unchanged for compatibility. */
export interface ExactPriceSignalWatchDraftV1 extends MarketSignalWatchDraftBaseV1 {
  signal_id: "underlying.price";
  /** Exact price remains symbol-scoped for compatibility. */
  selectors: { ticker: string };
  /** Signal-specific inputs distinct from the scalar threshold. */
  parameters: Record<string, never>;
  /** Inclusive scalar comparison. */
  operator: "gte" | "lte";
  /** Canonical decimal threshold text. */
  threshold: string;
}

/** Readable percentage input; the server derives signed scalar comparison. */
export interface PercentageMoveSignalWatchDraftV1 extends MarketSignalWatchDraftBaseV1 {
  signal_id:
    | "underlying.price_change.prior_regular_close"
    | "underlying.price_change.regular_session_open";
  /** Session-relative references require an exact canonical listing. */
  selectors: {
    ticker: string;
    mic_code: string;
    asset_type: "COMMON_STOCK" | "ETF";
    country_code: "US";
  };
  parameters: {
    direction: "UP" | "DOWN";
    magnitude_percent: string;
  };
}

export type MovingAverageRelationshipSignalId =
  | "underlying.completed_regular_close.relative_to_sma"
  | "underlying.completed_regular_close.crosses_sma"
  | "underlying.completed_regular_close.relative_to_ema"
  | "underlying.completed_regular_close.crosses_ema";

export type MovingAverageCrossoverSignalId =
  | "underlying.sma.crosses_sma"
  | "underlying.ema.crosses_ema";

interface MovingAverageSignalWatchDraftBaseV1 extends MarketSignalWatchDraftBaseV1 {
  selectors: {
    ticker: string;
    mic_code: string;
    asset_type: "COMMON_STOCK" | "ETF";
    country_code: "US";
  };
}

/** Customer-readable completed-close versus moving-average draft. */
export interface MovingAverageRelationshipSignalWatchDraftV1
  extends MovingAverageSignalWatchDraftBaseV1 {
  signal_id: MovingAverageRelationshipSignalId;
  parameters: {
    direction: "ABOVE" | "BELOW";
    window: "20" | "50" | "200";
  };
}

/** Customer-readable fast-versus-slow moving-average crossover draft. */
export interface MovingAverageCrossoverSignalWatchDraftV1
  extends MovingAverageSignalWatchDraftBaseV1 {
  signal_id: MovingAverageCrossoverSignalId;
  parameters: {
    direction: "ABOVE" | "BELOW";
    window_pair: "20_50" | "20_200" | "50_200";
  };
}

export type MomentumSignalId =
  | "underlying.rsi.relative_to_level"
  | "underlying.rsi.crosses_level"
  | "underlying.macd.line.crosses_signal"
  | "underlying.macd.line.crosses_zero"
  | "underlying.roc.relative_to_level"
  | "underlying.roc.crosses_level";

interface MomentumSignalWatchDraftBaseV1 extends MarketSignalWatchDraftBaseV1 {
  signal_id: MomentumSignalId;
  selectors: {
    ticker: string;
    mic_code: string;
    asset_type: "COMMON_STOCK" | "ETF";
    country_code: "US";
  };
}

export interface RsiSignalWatchDraftV1 extends MomentumSignalWatchDraftBaseV1 {
  signal_id: "underlying.rsi.relative_to_level" | "underlying.rsi.crosses_level";
  parameters: { direction: "ABOVE" | "BELOW"; level: string };
}

export interface MacdSignalWatchDraftV1 extends MomentumSignalWatchDraftBaseV1 {
  signal_id: "underlying.macd.line.crosses_signal" | "underlying.macd.line.crosses_zero";
  parameters: { direction: "ABOVE" | "BELOW" };
}

export interface RocSignalWatchDraftV1 extends MomentumSignalWatchDraftBaseV1 {
  signal_id: "underlying.roc.relative_to_level" | "underlying.roc.crosses_level";
  parameters: { direction: "ABOVE" | "BELOW"; level_percent: string };
}

export type CompletedDailyVolatilitySignalId =
  | "underlying.atr.completed_daily.14.relative_to_level"
  | "underlying.bollinger.percent_b.completed_daily.20_2.relative_to_level"
  | "underlying.bollinger.bandwidth.completed_daily.20_2.relative_to_level"
  | "underlying.realized_volatility.close_to_close.20d_annualized.relative_to_level";

export interface CompletedDailyVolatilitySignalWatchDraftV1
  extends MarketSignalWatchDraftBaseV1 {
  signal_id: CompletedDailyVolatilitySignalId;
  selectors: {
    ticker: string;
    mic_code: string;
    asset_type: "COMMON_STOCK" | "ETF";
    country_code: "US";
  };
  parameters:
    | { direction: "ABOVE" | "BELOW"; level_usd: string }
    | { direction: "ABOVE" | "BELOW"; level: string };
}

export type StandardizedOptionChainSignalId =
  | "options_chain.atm_iv.standardized_30d.relative_to_level"
  | "options_chain.iv_rv_spread.standardized_30d_20d.relative_to_level"
  | "options_chain.risk_reversal.standardized_30d_25delta.relative_to_level"
  | "options_chain.atm_iv_term_slope.standardized_60d_minus_30d.relative_to_level"
  | "options_chain.expected_move.standardized_30d.relative_to_level"
  | "options_chain.atm_quote_coverage.standardized_30d.relative_to_level"
  | "options_chain.atm_median_relative_spread.standardized_30d.relative_to_level";

/** Readable authoring draft for a server-standardized option-chain fact. */
export interface StandardizedOptionChainSignalWatchDraftV1
  extends MarketSignalWatchDraftBaseV1 {
  signal_id: StandardizedOptionChainSignalId;
  selectors: {
    ticker: string;
    mic_code: string;
    asset_type: "COMMON_STOCK" | "ETF";
    country_code: "US";
  };
  parameters: { direction: "ABOVE" | "BELOW"; level: string };
}

export type MarketSignalWatchDraftV1 =
  | ExactPriceSignalWatchDraftV1
  | PercentageMoveSignalWatchDraftV1
  | MovingAverageRelationshipSignalWatchDraftV1
  | MovingAverageCrossoverSignalWatchDraftV1
  | RsiSignalWatchDraftV1
  | MacdSignalWatchDraftV1
  | RocSignalWatchDraftV1
  | CompletedDailyVolatilitySignalWatchDraftV1
  | StandardizedOptionChainSignalWatchDraftV1;

const DECIMAL_PATTERN = /^(?:0|[1-9]\d*)(?:\.\d+)?$/;
const TICKER_PATTERN = /^[A-Z][A-Z0-9.-]{0,14}$/;
const MOVING_AVERAGE_WINDOWS = new Set(["20", "50", "200"]);
const MOVING_AVERAGE_WINDOW_PAIRS = new Set(["20_50", "20_200", "50_200"]);

function requireWholeNumber(value: number, label: string, allowZero = false) {
  if (!Number.isSafeInteger(value) || value < (allowZero ? 0 : 1)) {
    throw new Error(`${label} must be a ${allowZero ? "non-negative" : "positive"} whole number.`);
  }
  return value;
}

function buildPolicy(input: MarketSignalPolicyInput) {
  return {
    max_duration_days: requireWholeNumber(input.maxDurationDays, "Duration"),
    evaluation_interval_minutes: requireWholeNumber(
      input.evaluationIntervalMinutes,
      "Evaluation interval",
    ),
    max_alerts_per_watch: requireWholeNumber(input.maxAlertsPerWatch, "Alert limit"),
    cooldown_minutes: requireWholeNumber(input.cooldownMinutes, "Cooldown", true),
    market_hours_only: input.marketHoursOnly,
  };
}

function requirePositiveDecimal(
  value: string,
  descriptor: { minimum?: string; maximum?: string; max_decimal_places?: number } | undefined,
  errorMessage: string,
) {
  const decimalPlaces = value.includes(".") ? value.split(".")[1]?.length ?? 0 : 0;
  const numericValue = Number(value);
  const minimum = descriptor?.minimum === undefined ? null : Number(descriptor.minimum);
  const maximum = descriptor?.maximum === undefined ? null : Number(descriptor.maximum);
  if (!descriptor
    || !DECIMAL_PATTERN.test(value)
    || !Number.isFinite(numericValue)
    || numericValue <= 0
    || decimalPlaces > (descriptor.max_decimal_places ?? 12)
    || (minimum !== null && numericValue < minimum)
    || (maximum !== null && numericValue > maximum)) {
    throw new Error(errorMessage);
  }
  return numericValue;
}

function requireBoundedDecimal(
  value: string,
  descriptor: { minimum?: string; maximum?: string; max_decimal_places?: number } | undefined,
  errorMessage: string,
) {
  const signedDecimalPattern = /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/;
  const decimalPlaces = value.includes(".") ? value.split(".")[1]?.length ?? 0 : 0;
  const numericValue = Number(value);
  const minimum = descriptor?.minimum === undefined ? null : Number(descriptor.minimum);
  const maximum = descriptor?.maximum === undefined ? null : Number(descriptor.maximum);
  if (!descriptor
    || !signedDecimalPattern.test(value)
    || !Number.isFinite(numericValue)
    || decimalPlaces > (descriptor.max_decimal_places ?? 12)
    || (minimum !== null && numericValue < minimum)
    || (maximum !== null && numericValue > maximum)) {
    throw new Error(errorMessage);
  }
}

function momentumSignalId(draft: WatchMomentumDraft): MomentumSignalId | null {
  if (draft.indicator === "RSI") {
    return draft.triggerMode === "STATE"
      ? "underlying.rsi.relative_to_level"
      : draft.triggerMode === "CROSSING" ? "underlying.rsi.crosses_level" : null;
  }
  if (draft.indicator === "ROC") {
    return draft.triggerMode === "STATE"
      ? "underlying.roc.relative_to_level"
      : draft.triggerMode === "CROSSING" ? "underlying.roc.crosses_level" : null;
  }
  return draft.comparison === "SIGNAL_LINE"
    ? "underlying.macd.line.crosses_signal"
    : draft.comparison === "ZERO" ? "underlying.macd.line.crosses_zero" : null;
}

function movingAverageSignalId(
  draft: WatchMovingAverageDraft,
): MovingAverageRelationshipSignalId | MovingAverageCrossoverSignalId | null {
  if (!draft.averageType) return null;
  if (draft.comparison === "AVERAGE_CROSSOVER") {
    if (!draft.windowPair || !draft.direction) return null;
    return draft.averageType === "SMA"
      ? "underlying.sma.crosses_sma"
      : "underlying.ema.crosses_ema";
  }
  if (!draft.window || !draft.triggerMode || !draft.direction) return null;
  if (draft.averageType === "SMA") {
    return draft.triggerMode === "STATE"
      ? "underlying.completed_regular_close.relative_to_sma"
      : "underlying.completed_regular_close.crosses_sma";
  }
  return draft.triggerMode === "STATE"
    ? "underlying.completed_regular_close.relative_to_ema"
    : "underlying.completed_regular_close.crosses_ema";
}

/**
 * Build the exact customer-readable signal preview body.
 *
 * The function fails closed before transport when catalog authority, subject,
 * comparison, threshold, or schedule facts are missing. It deliberately never
 * constructs the server's internal evaluation representation.
 */
export function buildMarketSignalPreviewBody(
  input: MarketSignalPreviewInput,
): { signal_watch_draft: MarketSignalWatchDraftV1 } {
  const trendSignalId = input.movingAverageDraft
    ? movingAverageSignalId(input.movingAverageDraft)
    : null;
  const selectedMomentumSignalId = input.momentumDraft
    ? momentumSignalId(input.momentumDraft)
    : null;
  const selectedSignal = trendSignalId
    ? { signalId: trendSignalId, definitionVersion: 1 }
    : selectedMomentumSignalId
      ? { signalId: selectedMomentumSignalId, definitionVersion: 1 }
    : input.signal;
  if (!selectedSignal) throw new Error(
    input.movingAverageDraft
      ? "Complete the Moving averages setup and alert choices."
      : input.momentumDraft
        ? "Complete the Momentum setup and alert choices."
        : "Choose a market signal.",
  );
  const definition = getAuthorableWatchSignals(input.catalog, {
    job: "MARKET",
    subjectScope: "UNDERLYING",
  }).find((candidate) => (
    candidate.signal_id === selectedSignal.signalId
    && candidate.definition_version === selectedSignal.definitionVersion
  ));
  if (!definition) {
    throw new Error("That market signal is not available for this account.");
  }
  const presenter = getReviewedSignalPresenter(definition);
  if (!presenter) throw new Error("That market signal is not available for this account.");
  const ticker = input.ticker.trim().toUpperCase();
  if (!TICKER_PATTERN.test(ticker)) {
    throw new Error("Choose a valid ticker from the search results.");
  }
  const policy = buildPolicy(input.policy);

  if (input.momentumDraft) {
    const momentumDraft = input.momentumDraft;
    if (!selectedMomentumSignalId || !momentumDraft.direction) {
      throw new Error("Complete the Momentum setup and alert choices.");
    }
    if (policy.evaluation_interval_minutes !== 1440 || !policy.market_hours_only) {
      throw new Error("Momentum Watches check after each completed market day.");
    }
    const listingIdentity = normalizeWatchCanonicalListingIdentity(input.listingIdentity);
    if (!listingIdentity) {
      throw new Error("Choose an eligible active U.S. stock or ETF from the search results.");
    }
    const selectors = { ticker, ...listingIdentity };
    if (momentumDraft.indicator === "MACD") {
      return {
        signal_watch_draft: {
          schema_version: 1,
          job: "MARKET",
          signal_id: selectedMomentumSignalId as MacdSignalWatchDraftV1["signal_id"],
          definition_version: 1,
          subject_scope: "UNDERLYING",
          selectors,
          parameters: { direction: momentumDraft.direction },
          policy,
        },
      };
    }
    const parameterId = momentumDraft.indicator === "RSI" ? "level" : "level_percent";
    const value = momentumDraft.indicator === "RSI"
      ? momentumDraft.level.trim()
      : momentumDraft.levelPercent.trim();
    const descriptor = definition.parameters.find(
      (parameter) => parameter.parameter_id === parameterId && parameter.kind === "DECIMAL",
    );
    requireBoundedDecimal(
      value,
      descriptor,
      momentumDraft.indicator === "RSI"
        ? "Enter an RSI level greater than 0 and less than 100."
        : "Enter a rate-of-change percentage from -100 through 1,000.",
    );
    return momentumDraft.indicator === "RSI"
      ? {
        signal_watch_draft: {
          schema_version: 1,
          job: "MARKET",
          signal_id: selectedMomentumSignalId as RsiSignalWatchDraftV1["signal_id"],
          definition_version: 1,
          subject_scope: "UNDERLYING",
          selectors,
          parameters: { direction: momentumDraft.direction, level: value },
          policy,
        },
      }
      : {
        signal_watch_draft: {
          schema_version: 1,
          job: "MARKET",
          signal_id: selectedMomentumSignalId as RocSignalWatchDraftV1["signal_id"],
          definition_version: 1,
          subject_scope: "UNDERLYING",
          selectors,
          parameters: { direction: momentumDraft.direction, level_percent: value },
          policy,
        },
      };
  }

  if (isMovingAveragePresenter(presenter)) {
    const trendDraft = input.movingAverageDraft;
    if (!trendDraft
      || !["SMA", "EMA"].includes(trendDraft.averageType ?? "")
      || !["ABOVE", "BELOW"].includes(trendDraft.direction ?? "")) {
      throw new Error("Complete the Moving averages setup and alert choices.");
    }
    if (presenter.averageType !== trendDraft.averageType
      || (presenter.kind === "MOVING_AVERAGE_RELATIONSHIP"
        && (trendDraft.comparison !== "PRICE_TO_AVERAGE"
          || presenter.triggerMode !== trendDraft.triggerMode))
      || (presenter.kind === "MOVING_AVERAGE_CROSSOVER"
        && trendDraft.comparison !== "AVERAGE_CROSSOVER")) {
      throw new Error("That Moving averages rule does not match the reviewed signal contract.");
    }
    if (policy.evaluation_interval_minutes !== 1440 || !policy.market_hours_only) {
      throw new Error("Moving-average Watches check after each completed market day.");
    }
    const listingIdentity = normalizeWatchCanonicalListingIdentity(input.listingIdentity);
    if (!listingIdentity) {
      throw new Error("Choose an eligible active U.S. stock or ETF from the search results.");
    }
    const selectors = { ticker, ...listingIdentity };
    if (trendDraft.comparison === "PRICE_TO_AVERAGE") {
      if (!trendDraft.window
        || !MOVING_AVERAGE_WINDOWS.has(trendDraft.window)
        || !trendDraft.triggerMode
        || !["STATE", "CROSSING"].includes(trendDraft.triggerMode)) {
        throw new Error("Choose an average window and alert behavior.");
      }
      return {
        signal_watch_draft: {
          schema_version: 1,
          job: "MARKET",
          signal_id: definition.signal_id as MovingAverageRelationshipSignalId,
          definition_version: 1,
          subject_scope: "UNDERLYING",
          selectors,
          parameters: {
            direction: trendDraft.direction,
            window: trendDraft.window,
          },
          policy,
        },
      };
    }
    if (!trendDraft.windowPair || !MOVING_AVERAGE_WINDOW_PAIRS.has(trendDraft.windowPair)) {
      throw new Error("Choose a faster and slower average pair.");
    }
    return {
      signal_watch_draft: {
        schema_version: 1,
        job: "MARKET",
        signal_id: definition.signal_id as MovingAverageCrossoverSignalId,
        definition_version: 1,
        subject_scope: "UNDERLYING",
        selectors,
        parameters: {
          direction: trendDraft.direction,
          window_pair: trendDraft.windowPair,
        },
        policy,
      },
    };
  }
  if (isCompletedDailyVolatilityPresenter(presenter)) {
    if (policy.evaluation_interval_minutes !== 1440 || !policy.market_hours_only) {
      throw new Error("Completed-day volatility Watches check after each completed market day.");
    }
    if (!input.operator || !definition.operators.includes(input.operator)) {
      throw new Error("Choose at or above, or at or below.");
    }
    const listingIdentity = normalizeWatchCanonicalListingIdentity(input.listingIdentity);
    if (!listingIdentity) {
      throw new Error("Choose an eligible active U.S. stock or ETF from the search results.");
    }
    const threshold = input.threshold.trim();
    const descriptor = definition.parameters.find(
      (parameter) => parameter.parameter_id === presenter.thresholdParameter
        && parameter.kind === "DECIMAL",
    );
    requireBoundedDecimal(
      threshold,
      descriptor,
      presenter.measurement === "ATR14"
        ? "Enter an average true range from 0.000001 through 1,000,000, using up to six decimals."
        : presenter.measurement === "BOLLINGER_PERCENT_B_20_2"
          ? "Enter a Bollinger %B level from -100 through 100, using up to six decimals."
          : "Enter a percentage from 0 through 10,000%, using up to four decimal places.",
    );
    const common = {
      schema_version: 1 as const,
      job: "MARKET" as const,
      signal_id: definition.signal_id as CompletedDailyVolatilitySignalId,
      definition_version: 1 as const,
      subject_scope: "UNDERLYING" as const,
      selectors: { ticker, ...listingIdentity },
      policy,
    };
    const direction = input.operator === "gte" ? "ABOVE" as const : "BELOW" as const;
    return {
      signal_watch_draft: presenter.thresholdParameter === "level_usd"
        ? { ...common, parameters: { direction, level_usd: threshold } }
        : { ...common, parameters: { direction, level: threshold } },
    };
  }
  if (isStandardizedOptionChainPresenter(presenter)) {
    if (!input.operator || !definition.operators.includes(input.operator)) {
      throw new Error("Choose at or above, or at or below.");
    }
    const listingIdentity = normalizeWatchCanonicalListingIdentity(input.listingIdentity);
    if (!listingIdentity) {
      throw new Error("Choose an eligible active U.S. stock or ETF from the search results.");
    }
    const threshold = input.threshold.trim();
    const descriptor = definition.parameters.find(
      (parameter) => parameter.parameter_id === "level" && parameter.kind === "DECIMAL",
    );
    requireBoundedDecimal(
      threshold,
      descriptor,
      "Enter a value within the available range, using up to six decimals.",
    );
    return {
      signal_watch_draft: {
        schema_version: 1,
        job: "MARKET",
        signal_id: definition.signal_id as StandardizedOptionChainSignalId,
        definition_version: 1,
        subject_scope: "UNDERLYING",
        selectors: { ticker, ...listingIdentity },
        parameters: {
          direction: input.operator === "gte" ? "ABOVE" : "BELOW",
          level: threshold,
        },
        policy,
      },
    };
  }
  if (!input.operator || !definition.operators.includes(input.operator)) {
    throw new Error(presenter.kind === "PERCENTAGE_MOVE"
      ? "Choose Up or Down."
      : "Choose an available price direction.");
  }
  const threshold = input.threshold.trim();

  if (presenter.kind === "PERCENTAGE_MOVE") {
    const signalId = definition.signal_id;
    if (signalId !== "underlying.price_change.prior_regular_close"
      && signalId !== "underlying.price_change.regular_session_open") {
      throw new Error("That market signal is not available for this account.");
    }
    const magnitudeDescriptor = definition.parameters.find(
      (parameter) => parameter.parameter_id === "magnitude_percent"
        && parameter.kind === "DECIMAL",
    );
    requirePositiveDecimal(
      threshold,
      magnitudeDescriptor,
      "Enter a positive percentage within the available range and decimal precision.",
    );
    const listingIdentity = normalizeWatchCanonicalListingIdentity(
      input.listingIdentity,
    );
    if (!listingIdentity) {
      throw new Error(
        "Choose an eligible active U.S. stock or ETF from the search results.",
      );
    }
    return {
      signal_watch_draft: {
        schema_version: 1,
        job: "MARKET",
        signal_id: signalId,
        definition_version: 1,
        subject_scope: "UNDERLYING",
        selectors: { ticker, ...listingIdentity },
        parameters: {
          direction: input.operator === "gte" ? "UP" : "DOWN",
          magnitude_percent: threshold,
        },
        policy,
      },
    };
  }

  const thresholdDescriptor = definition.parameters.find(
    (parameter) => parameter.parameter_id === "threshold" && parameter.kind === "DECIMAL",
  );
  requirePositiveDecimal(
    threshold,
    thresholdDescriptor,
    "Enter a price within the available range and decimal precision.",
  );

  return {
    signal_watch_draft: {
      schema_version: 1,
      job: "MARKET",
      signal_id: "underlying.price",
      definition_version: 1,
      subject_scope: "UNDERLYING",
      selectors: { ticker },
      parameters: {},
      operator: input.operator,
      threshold,
      policy,
    },
  };
}
